import type {
  ApiInfrastructureErrors,
  BackendApiErrorUnion,
} from '../../../data/error/backendApi/_internal/createBackendApiError';
import { createBackendApiError } from '../../../data/error/backendApi/_internal/createBackendApiError';
import { liftRequest } from '../context';
import { BackendApi } from '../../../service/api/BackendApi';
import type { AuthToken } from '../../../service/auth/AuthToken';
import { ApiModule } from '../../../service/logger/ApiModule';
import type { LoggerConfig } from '../../../service/logger/LoggerConfig';
import { addResponseStatusToSpan } from '../../../utils/httpTracing';
import { logApiOperation } from '../../../utils/logger/apiLogger';
import type { ProductsOrderByT } from '@vs-shop/schemas/enum/productsOrderBy';
import {
  HydraCollectionResponse,
  type HydraCollectionResponseType,
} from '@vs-shop/schemas/openapi/response';
import type { AllFiltersState } from '@vs-shop/schemas/product/filter/state/all';
import { combineFiltersToApiQuery } from '@vs-shop/schemas/product/filter/stateToApi/combineFiltersToApiQuery';
import { schemaBodyJsonClean } from '@vs-shop/schemas/transform/schemaBodyJsonClean';
import type { DateTime, Schema as SchemaType } from 'effect';
import { Effect, Request, RequestResolver } from 'effect';
import type * as HttpClient from 'effect/unstable/http/HttpClient';
import * as HttpClientResponse from 'effect/unstable/http/HttpClientResponse';

import { getSearchOrderParams, HTTP_METHOD } from './_shared';

const ENDPOINT = '/api/v2/shop/search/products';
export type MakeGetSearchProductsDeps<SearchProducts extends SchemaType.Top> = {
  readonly schemas: {
    readonly searchProducts: SearchProducts;
  };
};

type GetSearchProductsProps = {
  q: string;
  page: number;
  itemsPerPage: number;
  filters: AllFiltersState;
  orderBy: ProductsOrderByT;
};

export const makeGetSearchProducts = <SearchProducts extends SchemaType.Top>(
  deps: MakeGetSearchProductsDeps<SearchProducts>
) => {
  const GetSearchProductsError = createBackendApiError({
    domain: 'Search',
    endpoint: 'GetSearchProducts',
    path: ENDPOINT,
    identifierField: 'q',
  });

  type SearchProductsRequestError = BackendApiErrorUnion<typeof GetSearchProductsError>;

  const getSearchProductsFetch = ({
    q,
    page,
    itemsPerPage,
    filters,
    orderBy,
  }: GetSearchProductsProps) =>
    Effect.gen(function* () {
      const apiClient = yield* BackendApi;
      const filtersPrepared = combineFiltersToApiQuery(filters);
      const orderParams = getSearchOrderParams(orderBy);

      return yield* apiClient
        .path(ENDPOINT)
        .method(HTTP_METHOD)
        .accept('application/ld+json')
        .fetch({
          query: {
            q,
            page,
            inStock: true,
            itemsPerPage,
            ...filtersPrepared,
            ...orderParams,
          },
        });
    }).pipe(
      Effect.withSpan(`http.${HTTP_METHOD} ${ENDPOINT}`, {
        kind: 'client',
        attributes: {
          'http.request.method': HTTP_METHOD,
          'search.query': q,
          'products.page': page,
          'products.itemsPerPage': itemsPerPage,
          'products.orderBy': orderBy,
        },
      })
    );

  const getSearchProducts = (props: GetSearchProductsProps) =>
    getSearchProductsFetch(props).pipe(
      Effect.map((response) => response.response),
      Effect.tap(addResponseStatusToSpan),
      Effect.withSpan('ValidateResponse'),
      Effect.flatMap(
        HttpClientResponse.matchStatus({
          '2xx': (response) =>
            schemaBodyJsonClean(HydraCollectionResponse(deps.schemas.searchProducts))(response),
          404: (response) =>
            Effect.fail(new GetSearchProductsError.NotFoundError({ q: props.q, response })),
          orElse: (response) =>
            Effect.fail(new GetSearchProductsError.ApiRequestError({ q: props.q, response })),
        })
      ),
      Effect.tap((result) =>
        logApiOperation(
          ApiModule.SearchProducts,
          'Debug',
          `Resolved ${String(result['hydra:member'].length)}/${String(result['hydra:totalItems'])} products for search "${props.q}", page ${String(props.page)}, itemsPerPage ${String(props.itemsPerPage)}`
        )
      ),
      Effect.withSpan('api.search.products', {
        kind: 'internal',
        attributes: {
          'search.query': props.q,
          'products.page': props.page,
          'products.itemsPerPage': props.itemsPerPage,
          'products.orderBy': props.orderBy,
        },
      })
    );

  type GetSearchProductsRequest = {
    readonly _tag: 'GetSearchProductsRequest';
    readonly q: string;
    readonly page: number;
    readonly itemsPerPage: number;
    readonly filters: AllFiltersState;
    readonly orderBy: ProductsOrderByT;
  } & Request.Request<
    HydraCollectionResponseType<typeof deps.schemas.searchProducts>,
    SearchProductsRequestError | ApiInfrastructureErrors,
    BackendApi | AuthToken | HttpClient.HttpClient | LoggerConfig | DateTime.CurrentTimeZone
  >;

  const GetSearchProductsRequest = Request.tagged<GetSearchProductsRequest>(
    'GetSearchProductsRequest'
  );

  const GetSearchProductsResolver = RequestResolver.fromEffect(
    liftRequest((request: GetSearchProductsRequest) =>
      getSearchProducts({
        q: request.q,
        page: request.page,
        itemsPerPage: request.itemsPerPage,
        filters: request.filters,
        orderBy: request.orderBy,
      })
    )
  );

  const requestSearchProducts = (props: GetSearchProductsProps) =>
    Effect.request(GetSearchProductsRequest(props), GetSearchProductsResolver);

  return { requestSearchProducts, GetSearchProductsError };
};
